import { create } from 'zustand';
import type { EightBallState } from '../engine/types';
import {
  createEightBallState,
  recordRackWin,
  undoLastRack,
  type RackWinInput,
} from '../engine/eightBallEngine';
import { games as gamesApi, frames as framesApi } from '../lib/api';
import { useSettingsStore } from './settingsStore';
import { usePricingStore, getActivePrice } from './pricingStore';

interface EightBallStoreState {
  gameState: EightBallState | null;
  gameId: number | null;
  tableId: number | null;
  pricePerRackSnapshot: number;
  rackStartTime: number | null;
  isGameActive: boolean;
  isRecording: boolean;

  startGame: (
    player1Name: string,
    player2Name: string,
    racksTarget: number,
    staffId: number,
    tableId?: number | null
  ) => Promise<void>;

  recordWin: (input: RackWinInput) => Promise<void>;
  undo: () => void;
  endGame: () => Promise<void>;
  replacePlayer: (playerIndex: 0 | 1, newName: string) => void;
  reset: () => void;
}

export const useEightBallStore = create<EightBallStoreState>((set, get) => ({
  gameState: null,
  gameId: null,
  tableId: null,
  pricePerRackSnapshot: 0,
  rackStartTime: null,
  isGameActive: false,
  isRecording: false,

  startGame: async (player1Name, player2Name, racksTarget, staffId, tableId) => {
    const settings = useSettingsStore.getState();
    const { rack: priceRack } = getActivePrice(usePricingStore.getState().slots, settings.getPricePerFrame(), settings.getPricePerRack());
    const game = await gamesApi.create({
      player1Name,
      player2Name,
      staffId,
      tableId: tableId ?? null,
      gameType: '8ball',
      framesTarget: racksTarget,
      pricePerFrameSnapshot: priceRack,
      currencySnapshot: settings.settings.currency,
    });
    set({
      gameState: createEightBallState(player1Name, player2Name, racksTarget),
      gameId: game.id,
      tableId: game.tableId ?? null,
      pricePerRackSnapshot: game.pricePerFrameSnapshot,
      rackStartTime: Date.now(),
      isGameActive: true,
    });
  },

  recordWin: async (input) => {
    const { gameState, gameId, rackStartTime, isRecording } = get();
    if (!gameState || !gameId || gameState.gameOver || isRecording) return;

    set({ isRecording: true });
    const durationSeconds = rackStartTime
      ? Math.floor((Date.now() - rackStartTime) / 1000)
      : undefined;

    const withDuration: RackWinInput = { ...input, durationSeconds };
    const nextState = recordRackWin(gameState, withDuration);

    try {
      await Promise.all([
        framesApi.create({
          gameId,
          frameNumber: gameState.rackNumber,
          winnerName: gameState.players[input.winnerIndex].name,
          player1Score: input.winnerIndex === 0 ? 1 : 0,
          player2Score: input.winnerIndex === 1 ? 1 : 0,
          durationSeconds,
          player1Balls: input.player1Balls ?? null,
          player2Balls: input.player2Balls ?? null,
        }),
        gamesApi.updateFrame(gameId, {
          player1Frames: nextState.players[0].racksWon,
          player2Frames: nextState.players[1].racksWon,
          framesPlayed: nextState.completedRacks.length,
        }),
      ]);
      set({ gameState: nextState, rackStartTime: Date.now(), isRecording: false });
    } catch (e) {
      set({ isRecording: false });
      throw e;
    }
  },

  undo: () => {
    const { gameState, isRecording } = get();
    if (!gameState || isRecording) return;
    set({ gameState: undoLastRack(gameState), rackStartTime: Date.now() });
  },

  endGame: async () => {
    const { gameState, gameId, pricePerRackSnapshot } = get();
    if (!gameState || !gameId) return;

    const p0 = gameState.players[0];
    const p1 = gameState.players[1];
    const racksPlayed = p0.racksWon + p1.racksWon;
    if (racksPlayed === 0) return;
    const winnerIndex = p0.racksWon > p1.racksWon ? 0 : 1;
    const loserIndex: 0 | 1 = winnerIndex === 0 ? 1 : 0;
    const amountOwed = racksPlayed * pricePerRackSnapshot;

    await gamesApi.complete(gameId, {
      winnerName: gameState.players[winnerIndex].name,
      loserName: gameState.players[loserIndex].name,
      amountOwed,
    });

    set({ gameState: null, gameId: null, tableId: null, pricePerRackSnapshot: 0, isGameActive: false });
  },

  reset: () => set({
    gameState: null, gameId: null, tableId: null,
    rackStartTime: null, isGameActive: false, isRecording: false,
  }),

  replacePlayer: (playerIndex, newName) => {
    const { gameState, gameId } = get();
    if (!gameState) return;
    const players = gameState.players.map((p, i) =>
      i === playerIndex ? { ...p, name: newName } : p
    ) as typeof gameState.players;
    set({ gameState: { ...gameState, players } });
    if (gameId) void gamesApi.updatePlayerName(gameId, playerIndex, newName);
  },
}));
